import React, { useState, useEffect, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { API, axios } from '../../utils/api';
import { useTraduzioni } from '../../i18n';
import { leggiToken } from '../../utils/sessione';
import { mostraBadge } from '../../utils/serviceWorker';
import { adessoRoma, leggiDataUtcRoma, quandoNovita } from '../../utils/percorso';

const POLL_INTERVAL = 30000; // 30 secondi

// Orario di una notifica come nelle Novità della home: "Oggi · 09:12", "Ieri · 16:05", "12 set · 11:30"
// nell'ora di Roma, come le Novità (anche con il telefono in un altro fuso). `lingua`: per il cliente russo o
// ucraino anche "Oggi", "Ieri" e il mese vanno nella sua lingua (di base italiano, che è quello che vede lo staff).
export const quandoNotifica = (sentAt, adesso = adessoRoma(), lingua = 'it') => {
  const d = leggiDataUtcRoma(sentAt);
  return d ? quandoNovita(d, adesso, lingua) : '';
};

// Campanella: conteggio delle notifiche nuove (ogni 30 secondi) ed elenco quando si apre. Il server (GET /notifiche)
// dà per ognuna titolo, testo, link (url) e letta; aprendo l'elenco si segnano tutte lette (le nuove restano
// evidenziate finché la tendina è aperta). La usano la barra dello staff (LayoutStaff) e quella del cliente (NavCliente).
// statoElenco: 'attesa' mentre l'elenco arriva, 'errore' se il server non risponde (la tendina lo dice, con
// "Riprovate": prima in tutti e due i casi diceva "Nessuna notifica per ora"), 'pronto'.
const useCampanella = () => {
  const { t } = useTraduzioni();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifiche, setNotifiche] = useState([]);
  const [statoElenco, setStatoElenco] = useState('attesa');

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifiche/unread-count`);
      const newCount = res.data.unread;
      
      // Se ci sono nuove notifiche, mostra notifica locale
      if (newCount > unreadCount && unreadCount !== 0 && 'Notification' in window && Notification.permission === 'granted') {
        // Mostra notifica locale come fallback push
        try {
          const notRes = await axios.get(`${API}/notifiche`);
          const latest = notRes.data.notifiche[0];
          if (latest) {
            // titolo e testo arrivano già tradotti dal server (backend/notifiche_testi)
            new Notification(latest.title || 'MK Client', {
              body: latest.body || t('comune.nuovo_aggiornamento'),
              icon: '/icone/icona-192.png',
              tag: 'mk-poll-notif'
            });
          }
        } catch (e) { /* skip */ }
      }
      
      setUnreadCount(newCount);
    } catch (e) { /* skip */ }
  }, [unreadCount, t]);

  useEffect(() => {
    const token = leggiToken();
    if (!token) return;

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Sull'app installata l'icona porta il numero dei non letti: lo stesso pallino della campanella. Con l'app
  // chiusa il numero lo mette la push (public/sw.js); qui si rimette quello giusto e si toglie quando si legge.
  useEffect(() => { mostraBadge(unreadCount); }, [unreadCount]);

  const caricaElenco = async () => {
    setStatoElenco('attesa');
    let elenco;
    try {
      const res = await axios.get(`${API}/notifiche`);
      elenco = Array.isArray(res.data?.notifiche) ? res.data.notifiche : [];
    } catch (e) {
      setStatoElenco('errore');
      return;
    }
    setNotifiche(elenco);
    setStatoElenco('pronto');
    // Segna come lette (le voci mostrate tengono "nuova" finché la tendina resta aperta)
    try {
      if (unreadCount > 0 || elenco.some((n) => n.letta === false)) await axios.post(`${API}/notifiche/mark-read`);
      setUnreadCount(0);
    } catch (e) { /* restano da leggere: il conteggio si aggiorna al prossimo giro */ }
  };

  const handleOpen = async () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) await caricaElenco();
  };

  return { unreadCount, showDropdown, setShowDropdown, notifiche, statoElenco, riprova: caricaElenco, handleOpen };
};

const NotificationPoller = () => {
  const { t } = useTraduzioni();
  const { unreadCount, showDropdown, notifiche, handleOpen } = useCampanella();

  const token = leggiToken();
  if (!token) return null;

  return (
    <div className="relative">
      <Button variant="ghost" size="sm" onClick={handleOpen} className="hover-gold relative h-9 w-9 p-0" data-testid="notification-bell">
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {showDropdown && (
        <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-card border border-gold/30 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gold/20">
            <h3 className="text-sm font-semibold text-gold">{t('comune.menu.notifiche')}</h3>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {notifiche.length === 0 ? (
              <p className="text-center text-white/40 text-sm py-6">{t('comune.nessuna_notifica')}</p>
            ) : (
              notifiche.map((n, idx) => (
                <a key={n.id || idx} href={n.url || '/'} className="block px-4 py-3 border-b border-gold/10 hover:bg-gold/5 transition-colors">
                  <p className="text-sm font-medium text-white">{n.title}</p>
                  <p className="text-xs text-white/60 mt-0.5">{n.body}</p>
                  <p className="text-[10px] text-white/30 mt-1">{quandoNotifica(n.sent_at)}</p>
                </a>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { NotificationPoller, useCampanella };
